// ----------------------------------------------------------------------------

export interface OrderItem {
  id: string;
  code: string;
  price: number;
  originalPrice?: number;
  discountPercent?: number;
  quantity: number;
  imageUrl?: string;
  productUid?: number;
  sku?: string;
  title?: string;
  note?: string;
  options?: string;
  attributes: {
    color: string;
    temperature: string;
    size: string;
  };
  tags?: string[];
  stockCount?: number;
  unlimited?: number;
}

export interface OrderRecord {
  id: string;
  clientId: string;
  date: string;
  customerName: string;
  email: string;
  total: string;
  subtotal?: string;
  cashDiscountPercent?: number;
  cashDiscountAmount?: string;
  items: OrderItem[];
  status?: number | string;
  notes?: string;
  message?: string;
  customerDiscountPercent?: number;
  customerTags?: string[];
  customerGroupName?: string;
  paymentTag?: string;
  paydescriptionTag?: string;
}

const LOCAL_STORAGE_KEY = "gea_luce_orders";

// ----------------------------------------------------------------------------

export function formatItemOptions(
  attributes?: { color?: string; temperature?: string; size?: string },
  _note?: string,
): string {
  const parts: string[] = [];
  if (attributes?.color && attributes.color !== "Standard" && attributes.color.trim()) {
    parts.push(`Colore: ${attributes.color.trim()}`);
  }
  if (
    attributes?.temperature &&
    attributes.temperature !== "Standard" &&
    attributes.temperature.trim()
  ) {
    parts.push(`Temperatura: ${attributes.temperature.trim()}`);
  }
  if (attributes?.size && attributes.size !== "Standard" && attributes.size.trim()) {
    parts.push(`Misura: ${attributes.size.trim()}`);
  }
  return parts.join(" | ");
}

export function parseItemOptions(optionsStr?: string): {
  attributes: { color: string; temperature: string; size: string };
  note?: string;
} {
  const defaultRes = {
    attributes: { color: "", temperature: "", size: "" },
    note: undefined,
  };

  if (!optionsStr || typeof optionsStr !== "string") {
    return defaultRes;
  }

  const str = optionsStr.trim();
  if (!str || str.toLowerCase() === "standard") return defaultRes;

  let color = "";
  let temperature = "";
  let size = "";
  const remainingNotes: string[] = [];

  // Split by pipe or newline
  const segments = str
    .split(/[|\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

  for (const segment of segments) {
    const lower = segment.toLowerCase();
    if (
      lower.startsWith("colore:") ||
      lower.startsWith("colore -") ||
      lower.startsWith("colore =")
    ) {
      const val = segment.replace(/^colore\s*[:=-]\s*/i, "").trim();
      if (val.toLowerCase() !== "standard") color = val;
    } else if (
      lower.startsWith("temperatura:") ||
      lower.startsWith("temperatura colore:") ||
      lower.startsWith("temperatura -") ||
      lower.startsWith("temperatura =")
    ) {
      const val = segment
        .replace(/^temperatura(\s*colore)?\s*[:=-]\s*/i, "")
        .trim();
      if (val.toLowerCase() !== "standard") temperature = val;
    } else if (
      lower.startsWith("misura:") ||
      lower.startsWith("taglia:") ||
      lower.startsWith("dimensione:") ||
      lower.startsWith("dimensione -")
    ) {
      const val = segment
        .replace(/^(misura|taglia|dimensione)\s*[:=-]\s*/i, "")
        .trim();
      if (val.toLowerCase() !== "standard") size = val;
    } else if (lower.startsWith("note:") || lower.startsWith("nota:")) {
      const n = segment.replace(/^not[ea]\s*[:=-]\s*/i, "").trim();
      if (n && n.toLowerCase() !== "standard") remainingNotes.push(n);
    } else {
      if (/^(2700k|3000k|4000k|5000k|6000k)$/i.test(segment)) {
        temperature = segment.toUpperCase();
      } else if (lower !== "standard") {
        remainingNotes.push(segment);
      }
    }
  }

  const note = remainingNotes.length > 0 ? remainingNotes.join(" | ") : undefined;

  return {
    attributes: {
      color: color && color.toLowerCase() !== "standard" ? color : "",
      temperature: temperature && temperature.toLowerCase() !== "standard" ? temperature : "",
      size: size && size.toLowerCase() !== "standard" ? size : "",
    },
    note: note || undefined,
  };
}

export function extractItemAttributesFromTags(tags?: string[]): {
  color: string;
  temperature: string;
  size: string;
} {
  const result = { color: "", temperature: "", size: "" };
  if (!tags || !Array.isArray(tags)) return result;

  const sizeLabels: Record<string, string> = {
    p: "Piccola (P)",
    m: "Media (M)",
    g: "Grande (G)",
    s: "Super (S)",
    x: "Extreme (X)",
  };

  for (const rawTag of tags) {
    const t = String(rawTag || "").trim().toLowerCase();
    if (t.startsWith("colore-")) {
      const val = rawTag.slice("colore-".length).trim();
      if (val && val.toLowerCase() !== "standard") {
        result.color = val
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }
    } else if (t.startsWith("temperatura-colore-")) {
      const val = rawTag.slice("temperatura-colore-".length).trim().toUpperCase();
      if (val && val.toLowerCase() !== "standard") {
        result.temperature = val;
      }
    } else if (t.startsWith("temperatura-")) {
      const val = rawTag.slice("temperatura-".length).trim().toUpperCase();
      if (val && val.toLowerCase() !== "standard") {
        result.temperature = val;
      }
    } else if (t.startsWith("dimensione-") || t.startsWith("taglia-") || t.startsWith("misura-")) {
      const sizeKey = t.replace(/^(dimensione-|taglia-|misura-)/, "").trim();
      result.size = sizeLabels[sizeKey] || sizeKey.toUpperCase();
    } else if (/^(2700k|3000k|4000k|5000k|6000k)$/i.test(t)) {
      result.temperature = t.toUpperCase();
    }
  }

  return result;
}

export const ORDER_STATUS_LABELS: Record<number, string> = {
  0: "In attesa di pagamento",
  1: "Pagato",
  2: "In allestimento",
  3: "Spedito",
  4: "Consegnato",
  5: "Chiuso",
  6: "Archiviato",
  7: "Annullato",
  8: "Post-vendita",
};

export function getOrderStatusLabel(status?: number | string): string {
  if (status === undefined || status === null || status === "") return "In allestimento";
  const num = Number(status);
  if (!isNaN(num) && ORDER_STATUS_LABELS[num]) {
    return ORDER_STATUS_LABELS[num];
  }
  return String(status);
}

export function extractCustomerTagValue(tags: string[] | undefined, prefix: string): string {
  if (!tags || !Array.isArray(tags)) return "";
  const lowerPrefix = prefix.toLowerCase().trim();
  const match = tags.find((t) => {
    const s = String(t).toLowerCase().trim();
    return (
      s.startsWith(`${lowerPrefix}-`) ||
      s.startsWith(`${lowerPrefix}:`) ||
      s.startsWith(`${lowerPrefix}=`)
    );
  });
  if (!match) return "";
  const sepIndex = match.search(/[-:=]/);
  if (sepIndex === -1) return "";
  return match.substring(sepIndex + 1).trim();
}

export function extractCashDiscountPercent(tags: string[] | undefined): number | undefined {
  if (!tags || !Array.isArray(tags)) return undefined;
  for (const t of tags) {
    const s = String(t).toLowerCase().trim();
    // match cashdiscount-5, cashdiscount:5, cashdiscount_5, cashdiscount5, cash-discount-5, scontopiede-5, scontocassa-5
    const match = s.match(/^(?:cash[-_]?discount|sconto[-_]?(?:piede|cassa))[-:=_]?(\d+(?:\.\d+)?)$/i);
    if (match && match[1]) {
      const num = parseFloat(match[1]);
      if (!isNaN(num) && num > 0) {
        return num;
      }
    }
  }
  return undefined;
}

export function extractCashDiscountTag(tags: string[] | undefined): string | undefined {
  if (!tags || !Array.isArray(tags)) return undefined;
  for (const t of tags) {
    const s = String(t).toLowerCase().trim();
    if (/^(?:cash[-_]?discount|sconto[-_]?(?:piede|cassa))[-:=_]?\d+/i.test(s)) {
      return String(t).trim();
    }
  }
  return undefined;
}

export function formatTotal(amount: number): string {
  const formatted = new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `€ ${formatted}`;
}

/**
 * Calcola il prezzo unitario scontato applicando la percentuale di sconto al prezzo base di listino.
 * Es. basePrice = 10, discountPercent = 40 => 6.00
 */
export function calculateDiscountedPrice(basePrice: number, discountPercent?: number): number {
  if (!discountPercent || discountPercent <= 0) return basePrice;
  const discounted = basePrice * (1 - discountPercent / 100);
  return Math.round(discounted * 100) / 100;
}

export function getOrders(): OrderRecord[] {
  if (typeof window === "undefined") {
    return [];
  }
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!stored) {
    return [];
  }
  try {
    const rawList = JSON.parse(stored);
    if (!Array.isArray(rawList)) {
      return [];
    }

    const normalized = rawList.map((raw: any) => {
      const items = Array.isArray(raw.items)
        ? raw.items.map((item: any) => {
            const rawOpt = item.options || item.note || "";
            const parsedOpt = parseItemOptions(rawOpt);
            const color =
              item.attributes?.color && item.attributes.color !== "Standard"
                ? String(item.attributes.color)
                : parsedOpt.attributes.color || String(item.attributes?.color || "Standard");
            const temp =
              item.attributes?.temperature && item.attributes.temperature !== "Standard"
                ? String(item.attributes.temperature)
                : parsedOpt.attributes.temperature || String(item.attributes?.temperature || "");
            const size =
              item.attributes?.size && item.attributes.size !== "Standard"
                ? String(item.attributes.size)
                : parsedOpt.attributes.size || String(item.attributes?.size || "");

            return {
              id: String(item.id || item.code || ""),
              code: String(item.code || ""),
              price: typeof item.price === "number" ? item.price : 0,
              originalPrice: typeof item.originalPrice === "number" ? item.originalPrice : undefined,
              discountPercent: typeof item.discountPercent === "number" ? item.discountPercent : undefined,
              quantity: typeof item.quantity === "number" ? item.quantity : 1,
              imageUrl: item.imageUrl || undefined,
              productUid: typeof item.productUid === "number" ? item.productUid : undefined,
              sku: item.sku || undefined,
              title: item.title || undefined,
              note:
                parsedOpt.note ||
                (item.note && item.note !== item.options ? item.note : undefined) ||
                undefined,
              options: rawOpt || undefined,
              attributes: {
                color,
                temperature: temp,
                size,
              },
              tags: Array.isArray(item.tags) ? item.tags : undefined,
              stockCount: typeof item.stockCount === "number" ? item.stockCount : undefined,
              unlimited: typeof item.unlimited === "number" ? item.unlimited : undefined,
            };
          })
        : [];

      return {
        id: String(raw.id || ""),
        clientId: String(raw.clientId || ""),
        date: String(raw.date || ""),
        customerName: String(raw.customerName || ""),
        email: String(raw.email || ""),
        total: String(raw.total || ""),
        items,
        status: raw.status,
        notes: raw.notes || raw.message || undefined,
        message: raw.message || raw.notes || undefined,
        customerDiscountPercent:
          typeof raw.customerDiscountPercent === "number"
            ? raw.customerDiscountPercent
            : undefined,
        customerTags: Array.isArray(raw.customerTags) ? raw.customerTags : undefined,
        customerGroupName: raw.customerGroupName || undefined,
        paymentTag: raw.paymentTag || undefined,
        paydescriptionTag: raw.paydescriptionTag || undefined,
      };
    });

    const filtered = normalized.filter((order) => {
      const s = Number(order.status);
      if (
        s === 6 ||
        s === 7 ||
        (typeof order.status === "string" &&
          (order.status.toLowerCase() === "cancelled" ||
            order.status.toLowerCase() === "annullato" ||
            order.status.toLowerCase() === "aborted" ||
            order.status.toLowerCase() === "archived"))
      ) {
        return false;
      }
      return true;
    });

    return filtered;
  } catch (error) {
    console.error("[Orders] Failed to parse stored orders:", error);
    return [];
  }
}

export function getOrderById(id: string): OrderRecord | undefined {
  const orders = getOrders();
  return orders.find((order) => order.id === id);
}

export function saveOrder(order: OrderRecord): void {
  const orders = getOrders();
  const index = orders.findIndex((o) => o.id === order.id);
  if (index >= 0) {
    orders[index] = order;
  } else {
    orders.unshift(order);
  }
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(orders));
}

export function saveOrders(orders: OrderRecord[]): void {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(orders));
}

export function deleteOrder(id: string): void {
  const orders = getOrders();
  const filtered = orders.filter((order) => order.id !== id);
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filtered));
}
